<template>
  <Teleport defer to="ion-app">
    <ion-modal
      ref="ionModal"
      v-bind="privateRegionAttrs"
      class="cgb-modal"
      v-theme-scope="themeScope"
      :can-dismiss="allowDismiss"
      :backdrop-dismiss="false"
      :show-backdrop="true"
      aria-labelledby="cg-block-title"
      @ionModalDidDismiss="onDidDismiss"
    >
      <!-- can-dismiss=false is what makes this a gate: it blocks the backdrop,
           the swipe-down and the Android hardware back button in one place,
           through Ionic's own overlay lifecycle rather than by omitting
           handlers. It is flipped open only when we dismiss deliberately. -->
      <div class="cgb-card">
        <div class="cgb-text">
          <h2 id="cg-block-title" class="cgb-title">Enter your CG to continue.</h2>
        </div>

        <form class="cgb-form" novalidate @submit.prevent="submit">
          <!-- `data-compact-input` is required, not decorative: the blanket
               `input[type='text']:not([data-compact-input])` rule in
               styles/main.scss forces `#ffffff !important` and a 55px height.
               White wins over `.cgb-input`'s transparent ground however specific
               that selector is, and in dark mode `--courses-ink-strong` is
               #F7F3EC — so what the member typed was invisible. -->
          <input
            ref="cgField"
            v-model="cgName"
            class="cgb-input"
            :class="{ 'cgb-input--error': !!errorMessage }"
            type="text"
            data-compact-input
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            placeholder="e.g. CG Alpha"
            :aria-invalid="!!errorMessage"
            aria-describedby="cg-block-error"
            :disabled="submitting"
            @input="onInput"
          />

          <p id="cg-block-error" class="cgb-error" role="alert">
            {{ errorMessage }}
          </p>

          <button type="submit" class="cgb-btn cgb-btn--primary" :disabled="!canSubmit">
            {{ submitting ? 'Checking…' : 'Continue' }}
          </button>
          <!-- Leaves Next Steps rather than dismissing in place. Someone who
               doesn't know their CG name needs a way out that isn't
               force-quitting the app; what they must not get is a way to keep
               reading with the question unanswered. -->
          <button
            type="button"
            class="cgb-btn cgb-btn--secondary"
            :disabled="submitting"
            @click="$emit('exit')"
          >
            Not now
          </button>
        </form>
      </div>
    </ion-modal>
  </Teleport>
</template>

<script>
import { IonModal } from '@ionic/vue';
import { CG_DOES_NOT_EXIST, isKnownCgName, resolveCgId } from '@/utils/cg-status';
import { CG_DIRECTORY_UNAVAILABLE } from '@/utils/member-split/unlinked-profile-copy';
import coursesTheme from '@/mixins/CoursesTheme';
import privateRegion from '@/mixins/PrivateRegion';

// Shown on a Next Steps program landing (and the lesson reader) when this member
// still has to confirm their connect group.
//
// It appears AFTER the unlock card has been spent and FULL has been granted —
// the grant is never held back waiting for this. That ordering is deliberate: a
// member who abandons this prompt keeps the access they paid for and gets asked
// again next time, instead of being left with a spent card and nothing to show.
//
// Cannot be dismissed in place: no backdrop handler, no close control, and no
// Escape. Lessons, the carousel and the buy CTA all sit behind it, so dismissing
// it would be a way to read the course with the question unanswered.
//
// It can, however, be LEFT. "Not now" navigates out of Next Steps entirely
// (added 2026-08-30). The gate is meant to stop someone reading the material
// unasked, not to trap a member who cannot remember their CG name — and without
// an exit their only escape was to force-quit the app. Leaving marks nothing as
// confirmed, so the prompt is there again on the way back in.
//
// Who sees it: anyone with no Connect Group on record, whatever their status —
// and nobody who has one (2026-09-03; until then V/N were re-asked every time).
// The rule lives in mixins/CgHardBlock.js, and "has a CG" is the server's
// answer where it gives one.
//
// A login waiting for its leader's link (profile.hasNoMember) has no member to
// link a CG to, so its answer moves the CG it claimed at registration instead
// (saveCg). Nothing on the card promises a link: that is the leader's to make.

export default {
  name: 'CgHardBlockModal',
  components: { IonModal },
  // privateRegion: teleported out of the page root, so the page's stamp does
  // not reach it — the attributes are bound on the modal host above.
  mixins: [coursesTheme, privateRegion],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['linked', 'exit'],
  data() {
    return {
      cgName: '',
      errorMessage: '',
      submitting: false,
      // Gates every route out of the modal — backdrop, swipe, Android back, and
      // programmatic dismiss alike. Opened only for a dismissal we are choosing
      // to make, which is why answering the question can still close it.
      allowDismiss: false,
    };
  },
  computed: {
    // The CG directory (name → id) backs the local check, exactly as it does in
    // LinkCgModal. fetchCgData swallows its own failures and leaves the map
    // empty, which isKnownCgName treats as "let the server decide".
    //
    // Read straight off state rather than through mapState('registration'): this
    // modal is mounted from several hosts and the registration module is not
    // guaranteed to be registered on all of them — the same reason the
    // CgHardBlock mixin optional-chains every store read it makes.
    cgData() {
      return this.$store.state.registration?.cgData || {};
    },
    canSubmit() {
      return !this.submitting && this.cgName.trim().length > 0;
    },
    // Optional-chained for the same reason as cgData: not every host registers
    // the profile module.
    hasNoMember() {
      return this.$store.state.profile?.hasNoMember === true;
    },
  },
  watch: {
    // `:is-open` silently fails to present when it flips after mount, so the
    // modal is driven programmatically — see docs/ION_MODAL_PATTERN.md.
    async modelValue(open) {
      if (open) {
        this.allowDismiss = false;
        // Load the CG directory the local check reads. Best-effort by design:
        // an empty map means the server stays the judge rather than the prompt
        // refusing everything. Caught explicitly — not awaited, so a rejection
        // would otherwise surface as an unhandled promise rejection.
        this.fetchCgDirectory();
        await this.presentModal();
        this.$nextTick(() => this.$refs.cgField?.focus());
        return;
      }
      // Our own dismissal: unlatch first, or can-dismiss blocks us too.
      this.allowDismiss = true;
      await this.$nextTick();
      await this.dismissModal();
    },
  },
  async mounted() {
    // The edge case the pattern doc calls out: a modal that starts open — which
    // never runs the watcher above, so the directory is fetched here as well.
    if (this.modelValue) {
      this.fetchCgDirectory();
      await this.presentModal();
    }
  },
  methods: {
    // Fire-and-forget, and deliberately tolerant of both shapes a dispatch can
    // hand back: Promise.resolve normalises a store that returns nothing, and the
    // catch keeps a rejection from surfacing as an unhandled promise rejection.
    fetchCgDirectory() {
      Promise.resolve(this.$store.dispatch('registration/fetchCgData')).catch(() => {});
    },
    // The overlay is driven programmatically because `:is-open` does not
    // reliably present after mount (docs/ION_MODAL_PATTERN.md). Guarded because
    // `$el` is a custom element: before it upgrades, present/dismiss are simply
    // not there yet, and an unguarded call throws inside a lifecycle hook.
    async presentModal() {
      const el = this.$refs.ionModal?.$el;
      if (typeof el?.present === 'function') await el.present();
    },
    async dismissModal() {
      const el = this.$refs.ionModal?.$el;
      if (typeof el?.dismiss === 'function') await el.dismiss();
    },
    onDidDismiss() {
      // Re-latch, so a modal that is presented again starts locked.
      this.allowDismiss = false;
    },
    onInput() {
      if (this.errorMessage) this.errorMessage = '';
    },
    async submit() {
      if (!this.canSubmit) return;
      // Refused here, without a round trip. "None" is an ACTIVE row in the CG
      // directory (id 0 — what the admin tool writes when staff remove a CG), so
      // isKnownCgName judges the match by the id it resolves to; otherwise the
      // one name meaning "I have no CG" would satisfy the very gate that exists
      // because this member has none. link-cg refuses the same id server-side,
      // so an empty directory still ends in the same answer.
      if (!isKnownCgName(this.cgName.trim(), this.cgData)) {
        this.errorMessage = CG_DOES_NOT_EXIST;
        return;
      }

      this.submitting = true;
      this.errorMessage = '';

      let result;
      try {
        result = await this.saveCg(this.cgName.trim());
      } catch (error) {
        // A 400 is the expected "no such CG" answer, not a crash. Either way the
        // member stays on the prompt — there is nowhere else for them to go.
        this.errorMessage = error?.response?.data?.message || CG_DOES_NOT_EXIST;
        this.submitting = false;
        return;
      }

      if (result?.error) {
        this.errorMessage = result.error;
        this.submitting = false;
        return;
      }

      this.submitting = false;
      this.cgName = '';
      this.$emit('linked', result);
    },
    // A member links its main CG. A login waiting for its link has no member, so
    // link-cg would refuse it (LINK_REQUIRED); it moves its claimed CG through the
    // profile's CG update instead, which takes the CG's id rather than its name.
    // Either way an `error` in the result keeps the prompt up, as a refusal does.
    saveCg(cgName) {
      if (!this.hasNoMember) return this.$store.dispatch('courses/linkMainCg', cgName);
      const cgId = resolveCgId(cgName, this.cgData);
      if (cgId === undefined) {
        this.fetchCgDirectory();
        return { error: CG_DIRECTORY_UNAVAILABLE };
      }
      return this.$store.dispatch('courses/claimLoginCg', { cgId, cgName });
    },
  },
};
</script>

<style scoped>
.cgb-modal {
  /* Sized as a centred alert rather than a sheet: this is a question, and it
     should not look like something that can be swiped away. */
  --width: min(340px, calc(100vw - 32px));
  --height: auto;
  --border-radius: 20px;
  --background: var(--courses-surface-raised);
  /* Heavier than the dismissable sheets: what is behind this is not readable
     content right now. */
  --backdrop-opacity: 0.75;
}

.cgb-card {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;
  padding: 28px;
  text-align: center;
  background-color: var(--courses-surface-raised);
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;
}

.cgb-text {
  display: flex;
  flex-direction: column;
}

.cgb-title {
  margin: 0;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.25;
  color: var(--courses-ink-strong);
}

.cgb-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cgb-input {
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1.5px solid var(--courses-ink-muted);
  background-color: transparent;
  color: var(--courses-ink-strong);
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  /* See the template note on data-compact-input. WKWebView draws its own inset
     chrome on a field it still considers native, which only becomes visible once
     the global white ground is gone. */
  -webkit-appearance: none;
  appearance: none;
  box-shadow: none;
}

.cgb-input::placeholder {
  color: var(--courses-ink-muted);
  opacity: 0.6;
  font-weight: 400;
}

.cgb-input:disabled {
  opacity: 0.6;
}

.cgb-input--error {
  border-color: #c4443a;
}

.cgb-error {
  min-height: 18px;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: #c4443a;
  text-align: left;
}

/* Both actions are the same pill; only the colours differ. Shared here rather
   than per variant so "Not now" cannot drift from "Continue" in size again —
   the secondary rule used to exist ONLY inside the two breakpoints, so below
   640px it fell back to the browser's default button. */
.cgb-btn {
  width: 100%;
  padding: 14px 0;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 700;
  /* A transparent hairline on both, so the bordered variant is not 2px taller
     than the filled one. */
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 100ms ease-out;
}

/* Matches the press feedback on every other tappable surface in this flow —
   the wallet's chips, the voucher cards, HaveACodeSheet's own buttons. */
.cgb-btn:active:not(:disabled) {
  transform: scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
  .cgb-btn {
    transition: none;
  }
}

.cgb-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.cgb-btn--primary {
  color: var(--courses-surface-raised);
  background-color: var(--courses-ink-strong);
}

/* The white version of the primary: the same raised surface as the card it sits
   on, with an edge to give it the pill's shape.
 *
 * The edge is `track`, NOT `border`. `--courses-border` is #37332F in dark mode
 * — the same value as `--courses-surface-raised` — so on this card the outline
 * would vanish and the button would read as bare text. That is the trap the
 * theme file documents for `--courses-handle` ("in dark mode that IS the panel
 * it sits on, so the thumb, its border and the track all resolved to the
 * background colour and the slider was invisible"). `track` is #D8D1CB / #646160
 * and stays visible against the card in both. */
.cgb-btn--secondary {
  color: var(--courses-ink-strong);
  background-color: var(--courses-surface-raised);
  border-color: var(--courses-track);
}

/* sm breakpoint (640px) */
@media (min-width: 640px) {
  .cgb-modal {
    --width: 380px;
  }
  .cgb-card {
    gap: 20px;
    padding: 36px;
  }
  .cgb-title {
    font-size: 24px;
  }
  .cgb-btn {
    padding: 16px 0;
    font-size: 16px;
  }
}

/* md breakpoint (768px) */
@media (min-width: 768px) {
  .cgb-modal {
    --width: 440px;
  }
  .cgb-card {
    padding: 40px;
  }
  .cgb-title {
    font-size: 30px;
  }
  .cgb-btn {
    padding: 20px 0;
    font-size: 18px;
  }
}
</style>
